import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab,
  Button,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SyncIcon from '@mui/icons-material/Sync';
import InteractionFilters from './InteractionFilters';
import InteractionSummary from './InteractionSummary';
import ButtonClicksReport from './ButtonClicksReport';
import FormSubmissionsReport from './FormSubmissionsReport';
import MapInteractionsReport from './MapInteractionsReport';
import PageViewsReport from './PageViewsReport';
import { exportInteractionsToCSV } from '../../api/userInteractionReports';
import { 
  isDenoKVBackendAvailable, 
  getPendingInteractionsCount, 
  getPendingSessionsCount,
  syncPendingInteractions,
  syncPendingSessions
} from '../../api/userInteractions';

/**
 * Component for displaying the interaction dashboard
 */
function InteractionDashboard() {
  const [currentTab, setCurrentTab] = useState(0);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    interactionTypes: [],
    userIds: [],
    components: []
  });
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [pendingInteractions, setPendingInteractions] = useState(0);
  const [pendingSessions, setPendingSessions] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  // Check if the DenoKV backend is available
  useEffect(() => {
    const checkBackendAvailability = async () => {
      try {
        const available = await isDenoKVBackendAvailable();
        setBackendAvailable(available);
      } catch (error) {
        console.error('Error checking backend availability:', error);
        setBackendAvailable(false);
      }
    };

    checkBackendAvailability();
    
    // Check for pending interactions and sessions
    const updatePendingCounts = () => {
      const interactionCount = getPendingInteractionsCount();
      const sessionCount = getPendingSessionsCount();
      setPendingInteractions(interactionCount);
      setPendingSessions(sessionCount);
    };
    
    // Update on mount
    updatePendingCounts();
    
    // Set up interval to check periodically
    const intervalId = setInterval(updatePendingCounts, 10000); // Check every 10 seconds
    
    // Set up event listeners for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Handle sync
  const handleSync = async () => {
    if (!isOnline || !backendAvailable || isSyncing) {
      return;
    }
    
    setIsSyncing(true);
    
    try {
      // Sync pending interactions
      const syncedInteractions = await syncPendingInteractions();
      
      // Sync pending sessions
      const syncedSessions = await syncPendingSessions();
      
      // Update pending counts
      setPendingInteractions(getPendingInteractionsCount());
      setPendingSessions(getPendingSessionsCount());
      
      console.log(`Synced ${syncedInteractions} interactions and ${syncedSessions} sessions`);
    } catch (error) {
      console.error('Error syncing pending data:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    setExportError(null);

    try {
      const csvContent = await exportInteractionsToCSV(filters);
      
      // Create a blob and download it
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `interaction-data-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting interactions:', error);
      setExportError('Failed to export interactions. Please try again later.');
    } finally {
      setExportLoading(false);
    }
  };

  // Render the current tab content
  const renderTabContent = () => {
    if (!backendAvailable) {
      return (
        <Alert severity="warning" sx={{ mt: 3 }}>
          The DenoKV backend is not available. Please start the Deno server to view interaction data.
        </Alert>
      );
    }

    switch (currentTab) {
      case 0:
        return <InteractionSummary filters={filters} />;
      case 1:
        return <PageViewsReport filters={filters} />;
      case 2:
        return <ButtonClicksReport filters={filters} />;
      case 3:
        return <FormSubmissionsReport filters={filters} />;
      case 4:
        return <MapInteractionsReport filters={filters} />;
      default:
        return <InteractionSummary filters={filters} />;
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Interaction Dashboard
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Connection Status */}
          <Chip 
            icon={isOnline ? <CloudDoneIcon /> : <CloudOffIcon />}
            label={isOnline ? 'Online' : 'Offline'}
            color={isOnline ? 'success' : 'error'}
            variant="outlined"
          />
          
          {/* Backend Status */}
          <Chip 
            icon={backendAvailable ? <CloudDoneIcon /> : <CloudOffIcon />}
            label={backendAvailable ? 'Server Available' : 'Server Unavailable'}
            color={backendAvailable ? 'success' : 'error'}
            variant="outlined"
          />
          
          {/* Pending Interactions */}
          {(pendingInteractions > 0 || pendingSessions > 0) && (
            <Chip 
              label={`${pendingInteractions + pendingSessions} Pending`}
              color="warning"
              variant="outlined"
            />
          )}
          
          {/* Sync Button */}
          {(pendingInteractions > 0 || pendingSessions > 0) && isOnline && backendAvailable && (
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={handleSync}
              disabled={isSyncing}
              color="warning"
              size="small"
            >
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}
        </Stack>
        
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={exportLoading || !backendAvailable}
        >
          {exportLoading ? 'Exporting...' : 'Export Data'}
        </Button>
      </Box>
      
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}
      
      {/* Pending Data Alert */}
      {(pendingInteractions > 0 || pendingSessions > 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          There are {pendingInteractions} interactions and {pendingSessions} sessions pending synchronization.
          {isOnline && backendAvailable ? 
            ' Click "Sync Now" to upload them to the server.' : 
            ' They will be automatically synced when you are online and the server is available.'}
        </Alert>
      )}
      
      <InteractionFilters filters={filters} onFilterChange={handleFilterChange} />
      
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Summary" />
          <Tab label="Page Views" />
          <Tab label="Button Clicks" />
          <Tab label="Form Submissions" />
          <Tab label="Map Interactions" />
        </Tabs>
      </Paper>
      
      {renderTabContent()}
    </Box>
  );
}

export default InteractionDashboard;
